import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  HmActionItem,
  ApprovalChainRequest,
  ApprovalChainView,
  ApprovalRoutePreview,
  ChainAppliesTo,
  ChainApproverChoices,
  ApprovalQueueItem,
  CreateRequisitionRequest,
  DecideApprovalInput,
  EmployeeExitResult,
  RecordExitRequest,
  RequisitionDetail,
  RequisitionRow,
  SlaRuleView,
  UpdateRequisitionRequest,
  UpdateSlaRuleInput,
} from '@nest-recruit/shared';
import { ApiError, apiGet, apiPatch, apiPost, request } from '../../../lib/api-client';

// S9: REQ-01 to REQ-05 and ADMIN-06. The two root keys are the ones the API's
// realtime hints name (`['requisitions']`, `['approvals']`), so a decision made by
// someone else refreshes this screen without a reload (non-negotiable #12).

export const REQUISITIONS_KEY = ['requisitions'] as const;
export const APPROVALS_KEY = ['approvals'] as const;
/** S10 — HM-01 and HM-02, under the same root so every hint and mutation refreshes them. */
export const HM_OPENINGS_KEY = [...REQUISITIONS_KEY, 'hm', 'openings'] as const;
export const HM_ACTIONS_KEY = [...REQUISITIONS_KEY, 'hm', 'actions'] as const;
export const CHAINS_KEY = ['admin', 'approval-chains'] as const;
export const SLA_RULES_KEY = ['admin', 'sla-rules'] as const;

export interface RequisitionListParams {
  readonly page: number;
  readonly pageSize: number;
  readonly query?: string;
  readonly status?: string;
  readonly priority?: string;
  readonly slaState?: string;
  readonly ventureId?: string;
  readonly departmentId?: string;
}

export function useRequisitions(params: RequisitionListParams) {
  return useQuery<{ rows: readonly RequisitionRow[]; total: number }, ApiError>({
    queryKey: [...REQUISITIONS_KEY, 'list', params],
    queryFn: async () => {
      const result = await request<readonly RequisitionRow[]>('/requisitions', { query: { ...params } });
      return { rows: result.data, total: result.meta?.total ?? result.data.length };
    },
    // Never blank a list the viewer can already see (CLAUDE.md §5a rule 2).
    placeholderData: (previous) => previous,
  });
}

export function useRequisition(id: string | null): UseQueryResult<RequisitionDetail | null, ApiError> {
  return useQuery<RequisitionDetail | null, ApiError>({
    queryKey: [...REQUISITIONS_KEY, 'detail', id],
    queryFn: () => (id ? apiGet<RequisitionDetail>(`/requisitions/${id}`) : Promise.resolve(null)),
    enabled: id !== null,
  });
}

export function useApprovalQueue(): UseQueryResult<readonly ApprovalQueueItem[], ApiError> {
  return useQuery<readonly ApprovalQueueItem[], ApiError>({
    queryKey: APPROVALS_KEY,
    queryFn: () => apiGet<readonly ApprovalQueueItem[]>('/approvals'),
    placeholderData: (previous) => previous,
  });
}

// --- Mutations ---------------------------------------------------------------------
//
// Each seeds the detail with what the server returns — the whole requisition after
// the change — and then awaits the invalidation, so nothing on screen jumps back to
// the stale copy in between (CLAUDE.md §5a rule 3).

function useRequisitionMutation<TInput>(run: (input: TInput) => Promise<RequisitionDetail>) {
  const queryClient = useQueryClient();
  return useMutation<RequisitionDetail, ApiError, TInput>({
    mutationFn: run,
    onSuccess: async (detail) => {
      queryClient.setQueryData([...REQUISITIONS_KEY, 'detail', detail.id], detail);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: REQUISITIONS_KEY }),
        queryClient.invalidateQueries({ queryKey: APPROVALS_KEY }),
      ]);
    },
  });
}

export function useCreateRequisition() {
  const queryClient = useQueryClient();
  return useMutation<{ id: string }, ApiError, CreateRequisitionRequest>({
    mutationFn: (input) => apiPost<{ id: string }>('/requisitions', input),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: REQUISITIONS_KEY });
    },
  });
}

export function useUpdateRequisition() {
  return useRequisitionMutation<{ id: string } & UpdateRequisitionRequest>(({ id, ...input }) =>
    apiPatch<RequisitionDetail>(`/requisitions/${id}`, input),
  );
}

export type LifecycleAction = 'submit' | 'revise' | 'hold' | 'resume' | 'cancel';

export function useRequisitionAction() {
  return useRequisitionMutation<{
    id: string;
    action: LifecycleAction;
    version: number;
    reason?: string;
  }>(({ id, action, version, reason }) =>
    apiPost<RequisitionDetail>(`/requisitions/${id}/${action}`, {
      version,
      ...(reason === undefined ? {} : { reason }),
    }),
  );
}

export function useAssignRecruiter() {
  return useRequisitionMutation<{ id: string; version: number; recruiterUserId: string }>(
    ({ id, ...input }) => apiPost<RequisitionDetail>(`/requisitions/${id}/recruiter`, input),
  );
}

/**
 * REQ-05. Optimistic on the queue: the decided item leaves the list at once and
 * comes back only if the server refuses (CLAUDE.md §5a rule 1).
 */
export function useDecideApproval() {
  const queryClient = useQueryClient();
  return useMutation<
    RequisitionDetail,
    ApiError,
    { stepId: string } & DecideApprovalInput,
    { previous: readonly ApprovalQueueItem[] | undefined; previousHm: readonly HmActionItem[] | undefined }
  >({
    mutationFn: ({ stepId, ...input }) =>
      apiPost<RequisitionDetail>(`/approvals/${stepId}/decision`, input),
    onMutate: async ({ stepId }) => {
      await queryClient.cancelQueries({ queryKey: APPROVALS_KEY });
      await queryClient.cancelQueries({ queryKey: HM_ACTIONS_KEY });
      const previous = queryClient.getQueryData<readonly ApprovalQueueItem[]>(APPROVALS_KEY);
      queryClient.setQueryData<readonly ApprovalQueueItem[]>(APPROVALS_KEY, (items) =>
        (items ?? []).filter((item) => item.stepId !== stepId),
      );
      // HM-02 lists the same step; it leaves there at once too.
      const previousHm = removeHmAction(queryClient, `approval:${stepId}`);
      return { previous, previousHm };
    },
    onError: (_error, _input, context) => {
      if (context?.previous) queryClient.setQueryData(APPROVALS_KEY, context.previous);
      if (context?.previousHm) queryClient.setQueryData(HM_ACTIONS_KEY, context.previousHm);
    },
    onSuccess: async (detail) => {
      queryClient.setQueryData([...REQUISITIONS_KEY, 'detail', detail.id], detail);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: REQUISITIONS_KEY }),
        queryClient.invalidateQueries({ queryKey: APPROVALS_KEY }),
      ]);
    },
  });
}

/**
 * Takes an item off My actions the moment it is acted on (CLAUDE.md §5a rule 1) and
 * returns the list as it was, for the rollback if the server refuses.
 */
export function removeHmAction(
  queryClient: QueryClient,
  key: string,
): readonly HmActionItem[] | undefined {
  const previous = queryClient.getQueryData<readonly HmActionItem[]>(HM_ACTIONS_KEY);
  if (previous) {
    queryClient.setQueryData<readonly HmActionItem[]>(
      HM_ACTIONS_KEY,
      previous.filter((item) => item.key !== key),
    );
  }
  return previous;
}

export function useRecordExit() {
  const queryClient = useQueryClient();
  return useMutation<EmployeeExitResult, ApiError, RecordExitRequest>({
    mutationFn: (input) => apiPost<EmployeeExitResult>('/employee-exits', input),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: REQUISITIONS_KEY });
      await queryClient.invalidateQueries({ queryKey: ['organisation'] });
      await queryClient.invalidateQueries({ queryKey: ['employees'] });
    },
  });
}

/**
 * §L41, bug B8: who a request sent from this department goes to (the HR check, then the chain's steps or Leadership), so Request a
 * hire shows the real route. Nothing is asked until a department is known.
 */
export function useRoutePreview(departmentId: string | null): UseQueryResult<ApprovalRoutePreview, ApiError> {
  return useQuery<ApprovalRoutePreview, ApiError>({
    queryKey: [...REQUISITIONS_KEY, 'route-preview', departmentId],
    queryFn: () => apiGet<ApprovalRoutePreview>('/requisitions/route-preview', { query: { departmentId: departmentId ?? '' } }),
    enabled: departmentId !== null,
    // A chain changes rarely; the route of the department shown keeps while another is picked.
    staleTime: 60_000,
    placeholderData: (previous) => previous,
  });
}

// --- ADMIN-06 and Time limits ---------------------------------------------------------
//
// Each read waits until the page knows it may ask (wave 1 review W15): nothing is asked of the server for a part that is off or a
// viewer who may not open the page.

export function useApprovalChains(enabled = true): UseQueryResult<readonly ApprovalChainView[], ApiError> {
  return useQuery<readonly ApprovalChainView[], ApiError>({
    queryKey: CHAINS_KEY,
    queryFn: () => apiGet<readonly ApprovalChainView[]>('/admin/approval-chains'),
    enabled,
  });
}

/** Only the roles and people who can decide a step of this kind, every one of them (no 200-person cap): the picker searches. */
export function useChainApprovers(appliesTo: ChainAppliesTo, enabled = true): UseQueryResult<ChainApproverChoices, ApiError> {
  return useQuery<ChainApproverChoices, ApiError>({
    queryKey: [...CHAINS_KEY, 'approvers', appliesTo],
    queryFn: () => apiGet<ChainApproverChoices>('/admin/approval-chains/approvers', { query: { appliesTo } }),
    enabled,
    staleTime: 60_000,
  });
}

/** Saves a chain. A PATCH answers with every chain, which is put on screen at once; a new one answers with its id. */
export function useSaveApprovalChain() {
  const queryClient = useQueryClient();
  return useMutation<{ id: string }, ApiError, { id: string | null; version: number } & ApprovalChainRequest>({
    mutationFn: async ({ id, version, ...input }) => {
      if (!id) return apiPost<{ id: string }>('/admin/approval-chains', input);
      const chains = await apiPatch<readonly ApprovalChainView[]>(`/admin/approval-chains/${id}`, { ...input, version });
      queryClient.setQueryData(CHAINS_KEY, chains);
      return { id };
    },
    // Refused as stale: fetch what the other Admin saved, so the page can offer "Load theirs" (the draft is kept).
    onError: async (error) => {
      if (error.status === 409) await queryClient.invalidateQueries({ queryKey: CHAINS_KEY });
    },
    onSuccess: async () => {
      // A chain decides the route Request a hire previews, so that goes stale with it.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: CHAINS_KEY }),
        queryClient.invalidateQueries({ queryKey: [...REQUISITIONS_KEY, 'route-preview'] }),
      ]);
    },
  });
}

export function useSlaRules(enabled = true): UseQueryResult<readonly SlaRuleView[], ApiError> {
  return useQuery<readonly SlaRuleView[], ApiError>({
    queryKey: SLA_RULES_KEY,
    queryFn: () => apiGet<readonly SlaRuleView[]>('/admin/sla-rules'),
    enabled,
  });
}

/** Saves one limit. The server answers with every limit, which goes on screen at once. */
export function useUpdateSlaRule() {
  const queryClient = useQueryClient();
  return useMutation<readonly SlaRuleView[], ApiError, { id: string } & UpdateSlaRuleInput>({
    mutationFn: ({ id, ...input }) => apiPatch<readonly SlaRuleView[]>(`/admin/sla-rules/${id}`, input),
    onSuccess: (rules) => {
      queryClient.setQueryData(SLA_RULES_KEY, rules);
    },
    // Refused as stale: fetch the other Admin's numbers, so the card can offer "Load theirs" (the typed numbers are kept).
    onError: async (error) => {
      if (error.status === 409) await queryClient.invalidateQueries({ queryKey: SLA_RULES_KEY });
    },
  });
}
